import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtCr, fmtDateShort, sinceLabel } from "@/lib/stock";
import { AlertTriangle } from "lucide-react";

export const TH = "px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";

export function Tile({ label, value, sub, tone, testId, big }) {
  return (
    <div className={`border bg-card rounded-md p-3 ${big ? "ring-1 ring-foreground/30" : ""}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`num font-bold mt-0.5 ${big ? "text-2xl" : "text-xl"} ${tone || ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{sub}</div>}
    </div>
  );
}

export function StockTiles({ t, buckets }) {
  if (!t) return null;
  const d7 = t.avr_delta7;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2" data-testid="stock-tiles">
      <Tile label="Units in stock" value={t.units.toLocaleString("en-IN")} sub={`${t.in_transit} in transit`} testId="stock-tile-units" />
      <Tile label="Stock value" value={fmtCr(t.value)} sub={`avg age ${t.avg_age ?? "—"} d · median ${t.median_age ?? "—"}`} testId="stock-tile-value" />
      <Tile label="Aged value at risk" value={fmtCr(t.avr)} big tone="text-red-700" testId="stock-tile-avr"
        sub={d7 == null ? `${t.aged_units} aged units` : <span className={d7 > 0 ? "text-red-600" : d7 < 0 ? "text-emerald-700" : ""}>{d7 > 0 ? "▲" : d7 < 0 ? "▼" : "•"} {fmtCr(Math.abs(d7))} vs 7 d ago · {t.aged_units} units</span>} />
      <Tile label="% aged" value={`${t.pct_aged_value ?? "—"}%`} sub={`value · ${t.pct_aged_units ?? "—"}% of units`} tone={t.pct_aged_value > 10 ? "text-red-700" : ""} testId="stock-tile-pct-aged" />
      <Tile label="Oldest unit" value={t.oldest ? `${t.oldest.age} d` : "—"} sub={t.oldest ? <Link to={`/stock/unit/${t.oldest.chassis}`} className="hover:underline">{t.oldest.model} · {t.oldest.dealer_code} · {sinceLabel(t.oldest.invoice_date)}</Link> : "no stock"} testId="stock-tile-oldest" />
      <Tile label="Est. carrying cost" value={`${fmtCr(t.carrying?.monthly)}/mo`} sub={`${fmtCr(t.carrying?.accumulated_on_aged)} accrued on aged · estimate`} testId="stock-tile-carrying" />
      <Tile label="Aged liquidated MTD" value={t.liquidated_mtd?.units ?? 0} sub={`${fmtCr(t.liquidated_mtd?.value)} · ${t.exits_mtd} exits, avg ${t.avg_exit_age_mtd ?? "—"} d`} tone="text-emerald-700" testId="stock-tile-liquidated" />
    </div>
  );
}

export function AgeingLadder({ ladder, onPick, active, title = "Ageing ladder" }) {
  if (!ladder?.length) return null;
  const max = Math.max(...ladder.map((b) => b.units), 1);
  const total = ladder.reduce((a, b) => a + b.units, 0);
  return (
    <div className="border bg-card rounded-md p-4" data-testid="stock-ladder">
      <h3 className="font-heading text-sm font-semibold mb-3">{title} <span className="text-muted-foreground font-normal text-xs">units · ₹ · click a bucket to filter</span></h3>
      <div className="space-y-2">
        {ladder.map((b) => (
          <button key={b.label} onClick={() => onPick?.(active === b.label ? null : b.label)} className={`w-full text-left group ${active && active !== b.label ? "opacity-50" : ""}`} data-testid={`stock-bucket-${b.label}`}>
            <div className="flex items-center justify-between text-[11px] mb-0.5">
              <span className="font-semibold flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: b.color }} />{b.label} days</span>
              <span className="num text-muted-foreground">{b.units} <span className="text-foreground font-semibold">({total ? Math.round((100 * b.units) / total) : 0}%)</span> · {fmtCr(b.value)}</span>
            </div>
            <div className="h-3 bg-secondary rounded-sm overflow-hidden"><div className="h-3 transition-[width] duration-300" style={{ width: `${(b.units / max) * 100}%`, background: b.color }} /></div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function RetailChip({ r }) {
  if (!r) return null;
  if (!r.through) return <span className="text-amber-800" data-testid="stock-retail-chip">Retail feed not loaded — velocity and days of supply unavailable</span>;
  return <span data-testid="stock-retail-chip">Retail through <b className="text-foreground">{fmtDateShort(r.through)}</b> · {r.loaded_months} months{r.stale && <span className="text-red-700 font-semibold"> · {r.lag_days} days behind the stock dump — upload today's YTD retail file</span>}{r.exceptions > 0 && <span className="text-amber-800"> · {r.exceptions} retailed-but-in-stock</span>}</span>;
}

export function FreshnessBanner({ d, compact }) {
  if (!d) return null;
  if (!d.latest_report_date) return <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2" data-testid="stock-no-data"><AlertTriangle size={14} /> No stock dump loaded yet — upload today's Stock Details file.</div>;
  const stale = d.stale;
  return (
    <div className={`rounded-md border px-3 py-1.5 text-[11px] flex flex-wrap items-center gap-x-3 gap-y-1 ${stale ? "border-amber-300 bg-amber-50 text-amber-900" : "bg-secondary/40 text-muted-foreground"}`} data-testid="stock-freshness">
      <span>Stock as of <b className="text-foreground">{fmtDateShort(d.data_as_of)}</b>{stale && <span> · ages computed for {fmtDateShort(d.as_of)} — no dump received since {fmtDateShort(d.latest_report_date)}</span>}</span>
      {!compact && d.missing_uploads?.length > 0 && <span className="text-red-700" data-testid="stock-missing-uploads">Missing dumps this month: {d.missing_uploads.map(fmtDateShort).join(", ")}</span>}
      {!compact && <RetailChip r={d.retail} />}
    </div>
  );
}

export function AgeCell({ u }) {
  return (
    <span className={`num ${u.aged ? "text-red-700 font-bold" : u.watch ? "text-amber-700 font-semibold" : ""}`} title={sinceLabel(u.invoice_date)}>
      {u.age} <span className="text-[10px] text-muted-foreground font-normal">{sinceLabel(u.invoice_date).replace("since ", "")}</span>
    </span>
  );
}

export function Pick({ value, onChange, options, all, width = "w-[130px]", testId }) {
  return (
    <Select value={value || "ALL"} onValueChange={(v) => onChange(v === "ALL" ? "" : v)}>
      <SelectTrigger className={`h-8 ${width} text-xs`} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="ALL">{all}</SelectItem>
        {options.map((o) => (typeof o === "string" ? <SelectItem key={o} value={o}>{o}</SelectItem> : <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>))}
      </SelectContent>
    </Select>
  );
}

export function StockFilters({ meta, f, setF, showBranch = true }) {
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const dates = [...(meta?.report_dates || [])].reverse();
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="stock-filters">
      <Select value={f.as_of || "LATEST"} onValueChange={(v) => setF({ ...f, as_of: v === "LATEST" ? "" : v })}>
        <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="stock-asof-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="LATEST">Today (live)</SelectItem>
          {dates.slice(0, 60).map((d) => <SelectItem key={d} value={d}>{fmtDateShort(d)} dump</SelectItem>)}
        </SelectContent>
      </Select>
      {showBranch && <Pick value={f.dealer} onChange={set("dealer")} options={(meta?.branches || []).map((b) => ({ v: b.code, l: `${b.code} · ${b.name}` }))} all="All branches" width="w-[190px]" testId="stock-branch-select" />}
      <Pick value={f.model} onChange={set("model")} options={meta?.models_in_stock || []} all="All models" testId="stock-model-select" />
      <Pick value={f.fuel} onChange={set("fuel")} options={meta?.fuels || []} all="All fuels" width="w-[110px]" testId="stock-fuel-select" />
      <Pick value={f.colour} onChange={set("colour")} options={meta?.colours || []} all="All colours" width="w-[170px]" testId="stock-colour-select" />
      <Pick value={f.bank} onChange={set("bank")} options={meta?.banks || []} all="All banks" width="w-[130px]" testId="stock-bank-select" />
    </div>
  );
}

export function MixTable({ rows, label, testId }) {
  if (!rows?.length) return null;
  return (
    <div className="border bg-card rounded-md overflow-auto max-h-[320px]" data-testid={testId}>
      <div className="px-3 py-2 border-b sticky top-0 bg-card"><h3 className="font-heading text-sm font-semibold">Ageing by {label}</h3></div>
      <table className="w-full text-xs">
        <thead className="border-b"><tr>{[label, "Units", "Aged", "% aged", "Value"].map((h, i) => <th key={h} className={`${TH} ${i ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b last:border-0 hover:bg-muted/50">
              <td className="px-2 py-1 font-medium">{r.key}</td>
              <td className="px-2 py-1 text-right num">{r.units}</td>
              <td className={`px-2 py-1 text-right num ${r.aged_units ? "text-red-700 font-semibold" : "text-muted-foreground"}`}>{r.aged_units}</td>
              <td className="px-2 py-1 text-right num">{r.pct_aged}%</td>
              <td className="px-2 py-1 text-right num text-muted-foreground">{fmtCr(r.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
